import {useDeferredValue} from 'react';
import {useDeferredData} from './useDeferredData';
import {PageType} from 'coreUtils/IUtils';

export const CURRENT_ROUTE_KEY = 'current-route';

export interface DeferredRoute {
	currentRoute: string | null;
	isHomePage: boolean;
	pageType: PageType | undefined;
	isPDP: boolean;
	isPLP: boolean;
	isSplashPage: boolean;
	is404: boolean;
	isEditorial: boolean;
	isMyAccount: boolean;
}

/**
 * Reads the full deferred route object resolved by `usePageInit`
 * during SSR. Suspends until the value is available.
 *
 * Wraps the value with `useDeferredValue` so that during a navigation
 * transition the old route stays visible until the transition commits,
 * preventing premature style/layout changes on the still-visible page.
 *
 * Use this in shell components (Header, Footer) that need route info
 * but live outside the page Suspense boundary.
 */
export function useDeferredRoute(): DeferredRoute {
	const route = useDeferredData<DeferredRoute>(CURRENT_ROUTE_KEY);

	return useDeferredValue(route);
}

/**
 * @deprecated Use `useDeferredRoute()` instead.
 *
 * Backward-compatible hook that returns the route string. Handles both the
 * legacy format (plain string) and the new `DeferredRoute` object during
 * the migration period.
 */
export default function useCurrentRoute(): string | null {
	const data = useDeferredData<DeferredRoute | string | null>(CURRENT_ROUTE_KEY);

	if (typeof data === 'object' && data !== null && 'currentRoute' in data) {
		return data.currentRoute;
	}

	return data as string | null;
}
